import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import Cropper from "react-easy-crop"
import { rotateImage } from "../../utils/ImageUtil"
import { ReactComponent as Clockwise } from "../../icon/rotate-right.svg"
import { ReactComponent as CounterClockwise } from "../../icon/rotate-left.svg"
import { Area } from "react-easy-crop/types"
import FaceGuide from "../../image/face-guide.png"
import BlueButton from "../buttons/BlueButton"

interface CropperModalProps {
  show: boolean
  showFaceGuide: boolean
  image: string
  onClose: () => void
  finishCrop: (cropRect: Area, image: string) => void
}

function CropperModal({ show, showFaceGuide, image, onClose, finishCrop }: CropperModalProps) {
  const [rotatedImage, setRotatedImage] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState<number>(1)
  const [cropRect, setCropRect] = useState<Area>({ x: 0, y: 0, width: 1, height: 1 })

  const { t } = useTranslation()

  const getImg = () => {
    const img = new Image()
    img.src = rotatedImage || image
    return img
  }

  const rotate = (degrees: number) => {
    setRotatedImage(rotateImage(getImg(), degrees))
  }

  return (
    <Dialog
      open={show}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: "0.7rem", color: "#323232" } } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", color: "#323232" }}>
        {t("crop.heading")}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, position: "relative", bgcolor: "#d3d3d3" }}>
        <Box sx={{ position: "relative", minWidth: 300, pb: "100%" }}>
          <Cropper
            image={rotatedImage || image}
            crop={crop}
            zoom={zoom}
            aspect={1}
            rotation={0}
            showGrid={!showFaceGuide}
            style={{
              cropAreaStyle: showFaceGuide
                ? {
                    background: `url(${FaceGuide}) no-repeat center center`,
                    backgroundSize: "cover",
                  }
                : {},
            }}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(cropRelative, cropRect) => setCropRect(cropRect)}
          />
        </Box>
        <Box sx={{ px: "45px", py: "10px" }}>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(_, value) => setZoom(value as number)}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 2, py: 1.5 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            onClick={() => rotate(-90)}
            sx={{
              width: 48,
              height: 48,
              background: "linear-gradient(90deg, #00d1f2 0%, #00a8e0 100%)",
              transition: "transform 0.2s",
              "&:hover": { transform: "scale(1.05)", background: "linear-gradient(90deg, #00d1f2 0%, #00a8e0 100%)" },
              "& svg": { fill: "white", width: 24, height: 24 },
            }}
          >
            <CounterClockwise />
          </IconButton>
          <IconButton
            onClick={() => rotate(90)}
            sx={{
              width: 48,
              height: 48,
              background: "linear-gradient(90deg, #00d1f2 0%, #00a8e0 100%)",
              transition: "transform 0.2s",
              "&:hover": { transform: "scale(1.05)", background: "linear-gradient(90deg, #00d1f2 0%, #00a8e0 100%)" },
              "& svg": { fill: "white", width: 24, height: 24 },
            }}
          >
            <Clockwise />
          </IconButton>
        </Box>
        <BlueButton onClick={() => finishCrop(cropRect, rotatedImage || image)}>
          {t("crop.save")}
        </BlueButton>
      </DialogActions>
    </Dialog>
  )
}

export default CropperModal
